import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { LayoutDashboard, BookOpen, Salad, Users, Settings, LogOut, Shield, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/dashboard", label: "الرئيسية", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/courses", label: "الدورات", icon: BookOpen, testid: "nav-courses" },
  { to: "/recipes", label: "الوصفات الصحية", icon: Salad, testid: "nav-recipes" },
  { to: "/ai-chat", label: "شات الذكاء", icon: Bot, testid: "nav-ai-chat" },
  { to: "/community", label: "المجتمع", icon: Users, testid: "nav-community" },
  { to: "/settings", label: "الإعدادات", icon: Settings, testid: "nav-settings" },
];

export default function MemberLayout() {
  const { logout, hasRole } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header (desktop + mobile) */}
      <header className="sticky top-0 z-40 glass border-b">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-xl bg-primary text-primary-foreground grid place-items-center font-display font-bold">م</div>
            <span className="font-display text-base sm:text-lg font-semibold truncate">منصة التدريب</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hasRole("admin", "super_admin", "moderator") && (
              <Button variant="outline" size="sm" onClick={() => nav("/admin")} data-testid="btn-open-admin" className="gap-2">
                <Shield className="h-4 w-4" /> <span className="hidden sm:inline">لوحة الإدارة</span>
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => nav("/settings")} data-testid="btn-open-settings" aria-label="الإعدادات">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="btn-logout" aria-label="تسجيل الخروج">
              <LogOut className="h-4 w-4 rtl:-scale-x-100" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-10 flex gap-8">
        {/* Desktop side nav on RIGHT (for RTL, it appears first in flex which visually is right side) */}
        <aside className="hidden lg:block w-64 shrink-0">
          <nav className="sticky top-24 space-y-1" data-testid="side-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-secondary"
                  }`
                }
              >
                <l.icon className="h-4 w-4 rtl:-scale-x-100" />
                <span>{l.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 pb-24 lg:pb-0">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t" data-testid="bottom-nav">
        <div className="grid grid-cols-5">
          {links.slice(0, 5).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`bottom-${l.testid}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center py-2 gap-1 text-[10px] leading-tight ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <l.icon className="h-5 w-5 rtl:-scale-x-100" />
              <span className="whitespace-nowrap">{l.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
